import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const PricingPage = () => {
  const engagements = [
    {
      name: 'Discovery Scan',
      price: 'Free',
      description: 'Surface-level automated diagnostic across the four trust domains. The fastest way to know where to look next.',
      features: [
        '10-minute structured assessment',
        'Evidence Integrity evaluation',
        'Identity Credibility check',
        'Operational Visibility review',
        'Control Architecture analysis',
        'AI-generated trust report'
      ],
      cta: 'Run Discovery Scan',
      link: '/scan',
      highlighted: false
    },
    {
      name: 'Structured Review',
      price: '£295',
      description: 'Expert investigation of one critical flow, such as onboarding, checkout, payments or identity. Delivered as a written QA Radar report within 5 working days. Everything documented. Nothing left to interpretation.',
      features: [
        'One critical flow investigated end to end',
        'Written QA Radar report',
        '5 working day turnaround',
        'Async only. No calls required',
        'Every finding tied to evidence'
      ],
      cta: 'Start a Structured Review',
      link: '/contact',
      highlighted: false
    },
    {
      name: 'Pre-Release QA Audit',
      price: '£750',
      description: 'Full trust and risk audit before you ship. Covers all critical flows, edge cases and failure patterns. Delivered as a prioritised QA Radar report with 14 days of direct follow-up access via Telegram.',
      features: [
        'All critical flows audited',
        'Edge cases and failure patterns mapped',
        'Prioritised QA Radar report',
        '14 days of Telegram follow-up access',
        'Async only. No calls required'
      ],
      cta: 'Book a Pre-Release Audit',
      link: '/contact',
      highlighted: true
    },
    {
      name: 'Advisory Retainer',
      price: '£500',
      priceSuffix: '/month',
      smallPrint: '3 month minimum engagement',
      description: 'Ongoing trust architecture oversight as your product evolves. Delta reports on every major release, monthly written judgment review, direct Telegram access, quarterly deep audit and unlimited Trust Signal Scans. You get a trust partner, not a report.',
      features: [
        'Delta report on every major release',
        'Monthly written judgment review',
        'Direct Telegram access',
        'Quarterly deep audit',
        'Unlimited Trust Signal Scans'
      ],
      cta: 'Talk about a retainer',
      link: '/contact',
      highlighted: false
    }
  ];

  const enhancements = [
    { name: 'Delta Report', price: '£95 to £145', description: 'Compare previous vs current trust signals' },
    { name: 'Priority Review', price: '+£75', description: '24-hour turnaround' },
    { name: 'Extended Analysis', price: '+£95', description: 'Deeper breakdown with edge case testing' }
  ];

  return (
    <div className="min-h-screen bg-[#0B0B0D] flex flex-col">
      <SEO
        title="Pricing | Subsurface. Structured Review, Pre-Release Audit, Advisory Retainer"
        description="Four ways to engage Subsurface. Free Discovery Scan, £295 Structured Review, £750 Pre-Release QA Audit, or £500/month Advisory Retainer. All async. No calls required."
        path="/pricing"
      />
      <Navigation />

      <div className="flex-1 pt-32 pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-6xl font-bold font-display mb-6">How to Engage Subsurface</h1>
            <p className="text-lg text-[#A0A0A0] max-w-3xl mx-auto leading-relaxed">
              Structured, output-driven engagements. All async. No calls required.
            </p>
          </motion.div>

          {/* Core Engagements */}
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 mb-16">
            {engagements.map((engagement, idx) => (
              <motion.div
                key={engagement.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className={`glass-card p-8 rounded-lg flex flex-col ${
                  engagement.highlighted ? 'border-[#FF6A00] border-2' : ''
                }`}
                data-testid={`tier-${engagement.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                {engagement.highlighted && (
                  <div className="text-xs font-mono uppercase tracking-widest text-[#FF6A00] mb-3">Most chosen</div>
                )}
                <h3 className="text-2xl font-display font-bold mb-2">{engagement.name}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-[#FF6A00]">{engagement.price}</span>
                  {engagement.priceSuffix && (
                    <span className="text-lg text-[#A0A0A0]">{engagement.priceSuffix}</span>
                  )}
                  {engagement.smallPrint && (
                    <div className="text-xs text-[#A0A0A0] mt-1">{engagement.smallPrint}</div>
                  )}
                </div>
                <p className="text-sm text-[#A0A0A0] mb-6">{engagement.description}</p>

                <ul className="space-y-3 mb-8 flex-1">
                  {engagement.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" />
                      <span className="text-[#A0A0A0]">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={engagement.link}
                  className={`block text-center px-6 py-3 rounded-md font-semibold transition-all ${
                    engagement.highlighted
                      ? 'bg-[#FF6A00] text-white hover:bg-[#FF6A00]/90'
                      : 'bg-transparent border border-[#FF6A00]/30 text-white hover:bg-[#FF6A00]/10'
                  }`}
                  data-testid={`tier-cta-${engagement.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  {engagement.cta}
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Optional Enhancements */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-card p-8 rounded-lg"
          >
            <h2 className="text-2xl font-display font-bold mb-6">Optional Enhancements</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {enhancements.map((enhancement) => (
                <div key={enhancement.name} className="border-l-2 border-[#FF6A00] pl-4">
                  <h3 className="text-lg font-semibold mb-1">{enhancement.name}</h3>
                  <div className="text-xl font-bold text-[#FF6A00] mb-2">{enhancement.price}</div>
                  <p className="text-sm text-[#A0A0A0]">{enhancement.description}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PricingPage;
